import { useEffect, useRef, useState } from 'react';
import { asset } from '@/lib/paths.ts';
import { BannerPill } from '../BannerPill.tsx';
import { useBanner } from '../useBanner.ts';
import { getQueryParam } from '../utils.ts';

/**
 * Charity of Champions 2026 commemorative coin banner.
 *
 * Triggered when `?indentifier=COC2026` (or `?identifier=COC2026`) is present
 * in the URL. QR codes on physical commemorative coins point to this URL.
 *
 * Built on the shared banner lifecycle (`useBanner`) -- this file owns only
 * the reveal animation and the ribbon/coin visuals; idle/started/landed/
 * minimized state, the auto-start and auto-minimize timers, hover-to-pause,
 * and minimizing on an outside click all come from there.
 *
 * On mount the coin sits at rest, spinning slowly and glowing gently, and
 * waits for a click -- or plays itself after 6s of no click. Either way, the
 * full reveal is the same: the coin spins in
 * place (sideways, alternating faces via a horizontal squash -- not a flat
 * disc rotation, which would read as "rolling"), drifts left while it
 * decelerates, pauses, hops, then rolls right to unroll a ribbon banner that
 * was hidden behind it the whole time.
 *
 * Once landed it stays fully expanded for a while, then tucks itself back
 * into a small pill badge (coin + "COC 2026") -- never fully disappearing,
 * so there's always something to click back open. Hovering or touching the
 * expanded banner pauses that countdown; clicking anywhere else on the page
 * minimizes it right away, same as the countdown eventually would. Clicking
 * the pill restores it, and clicking the coin while expanded visits the
 * sponsor site (in a new tab) and minimizes it.
 */

const FRONT_SRC = asset('images/coin-front.webp');
const BACK_SRC = asset('images/coin-back.webp');
const SPONSOR_URL = 'https://www.charityofchampionsutah.com/';

const COIN_SIZE = 76; // px, also the source images' intrinsic display size

// Ribbon width == ROLL_DISTANCE, so the coin lands exactly at its cap. We
// want the coin's own LEFT edge at its pause point to sit the same distance
// from the viewport's left edge as the coin's own RIGHT edge sits from the
// viewport's right edge once it lands -- the same measurement (coin edge to
// viewport edge) on both sides, not a ribbon edge on one side and a coin
// edge on the other. Pause is DRIFT_DISTANCE left of center; landing is
// (ROLL_DISTANCE - DRIFT_DISTANCE) right of center. Since both distances add
// the same COIN_RADIUS to get from center-line to visible edge, equating the
// two clearances cancels the radius out entirely:
//   DRIFT_DISTANCE = ROLL_DISTANCE - DRIFT_DISTANCE
//   DRIFT_DISTANCE = ROLL_DISTANCE / 2
const ROLL_DISTANCE = 264;
const DRIFT_DISTANCE = ROLL_DISTANCE / 2;

// The ribbon's rendered left edge extends further left than the coin's
// pause point, purely as a visual matter -- the coin's position, the roll
// distance, and the reveal timing (which only cares about the ROLL_DISTANCE-
// wide section nearest the coin) are all unaffected. This gives the text
// more breathing room and, sized to COIN_RADIUS, makes the ribbon's new
// left margin equal the coin's right margin at rest (see the CSS for why
// that specific value).
const RIBBON_EXTRA_LEFT = COIN_SIZE / 2;

// Spin-down in place, drifting left. 5.5 turns (not a whole number) --
// squash is symmetric, so a whole number of turns lands back on the SAME
// face it started on. An odd number of half-turns lands it on the *other*
// (front/Lux) face at rest, with no jarring last-instant swap.
const SPIN_DURATION = 1650;
const SPIN_TOTAL_ANGLE = 1980;
const SPIN_W0 = (2 * SPIN_TOTAL_ANGLE) / SPIN_DURATION;
const SPIN_K = SPIN_W0 / SPIN_DURATION;

const PAUSE_DURATION = 200; // beat of stillness before the jump
const JUMP_DURATION = 260; // little hop before it rolls off
const JUMP_HEIGHT = 16;
const ROLL_DURATION = 620; // roll right to unroll the ribbon
const ROLL_TURNS = 2; // whole rotations, lands right-side up

const T_SPIN_END = SPIN_DURATION;
const T_PAUSE_END = T_SPIN_END + PAUSE_DURATION;
const T_JUMP_END = T_PAUSE_END + JUMP_DURATION;
const T_ROLL_END = T_JUMP_END + ROLL_DURATION;

const AUTO_MINIMIZE_DELAY = 9000; // ms, counted from when the roll finishes

const IDLE_SPIN_PERIOD = 2600; // ms per full revolution while waiting for a click
const AUTO_START_DELAY = 6000; // ms of idling before the animation plays itself

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

export function CharityOfChampions2026Banner() {
  const [active, setActive] = useState(false);
  const {
    sceneRef,
    started,
    setStarted,
    landed,
    setLanded,
    minimized,
    setMinimized,
    expanded,
    containerProps,
  } = useBanner({
    active,
    autoStartDelay: AUTO_START_DELAY,
    autoMinimizeDelay: AUTO_MINIMIZE_DELAY,
  });

  const coinWrapRef = useRef<HTMLButtonElement>(null);
  const coinRef = useRef<HTMLDivElement>(null);
  const frontImgRef = useRef<HTMLImageElement>(null);
  const backImgRef = useRef<HTMLImageElement>(null);
  const rimGlowRef = useRef<HTMLDivElement>(null);
  const ribbonRef = useRef<HTMLDivElement>(null);
  const tableShadowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (getQueryParam(['indentifier', 'identifier']) === 'COC2026') {
      setActive(true);
    }
  }, []);

  useEffect(() => {
    if (!active) return;

    const raw = {
      scene: sceneRef.current,
      coinWrap: coinWrapRef.current,
      coin: coinRef.current,
      frontImg: frontImgRef.current,
      backImg: backImgRef.current,
      rimGlow: rimGlowRef.current,
      ribbon: ribbonRef.current,
      tableShadow: tableShadowRef.current,
    };
    if (Object.values(raw).some((el) => el === null)) return;
    // TypeScript does not carry control-flow narrowing from the check above
    // into nested closures below, even for `const` -- asserting the type
    // once here (rather than a `!` at every access site) is what keeps the
    // rest of this effect free of non-null assertions.
    const { scene, coinWrap, coin, frontImg, backImg, rimGlow, ribbon, tableShadow } =
      raw as {
        [K in keyof typeof raw]: NonNullable<(typeof raw)[K]>;
      };

    // Both faces are already decoded, stacked <img> elements -- swapping
    // which one is visible is a pure compositor opacity toggle. Reassigning
    // a single <img>'s `src` every time the coin flips (roughly a dozen
    // times over the spin, continuously while idle) forces a decode/repaint
    // on each swap, which is what was making the animation stutter.
    function showFace(back: boolean) {
      frontImg.style.opacity = back ? '0' : '1';
      backImg.style.opacity = back ? '1' : '0';
    }

    let rafId: number | null = null;
    let startTime: number | null = null;

    function layoutCenteredAssembly() {
      const sceneWidth = scene.getBoundingClientRect().width;
      // Where the coin itself sits at rest (pause point and shadow) --
      // unaffected by how far the ribbon's own left edge extends.
      const restPoint = sceneWidth / 2 - DRIFT_DISTANCE;
      ribbon.style.left = `${restPoint - RIBBON_EXTRA_LEFT}px`;
      tableShadow.style.left = `${restPoint}px`;
    }

    // Before the click, the coin hasn't drifted anywhere yet -- it sits at
    // true center (coin-wrap's own default position), so only the shadow
    // needs positioning under it. The ribbon stays fully clipped either way.
    function layoutIdle() {
      const sceneWidth = scene.getBoundingClientRect().width;
      tableShadow.style.left = `${sceneWidth / 2}px`;
    }

    function idleFrame(now: number) {
      if (startTime === null) startTime = now;
      const t = now - startTime;

      // Same face-swap squash as the real spin-down, but at a constant
      // speed forever -- there's no deceleration to reach, since this loop
      // just keeps going until the click hands off to play().
      const angle = (360 / IDLE_SPIN_PERIOD) * t;
      const rad = (angle * Math.PI) / 180;
      const squash = Math.cos(rad);
      showFace(squash >= 0);
      coin.style.transform = `scaleX(${Math.abs(squash).toFixed(4)})`;

      rafId = requestAnimationFrame(idleFrame);
    }

    function frame(now: number) {
      if (startTime === null) startTime = now;
      const t = now - startTime;

      if (t <= T_SPIN_END) {
        // Spin sideways in place -- horizontal squash alternating the two
        // faces, not a flat disc rotation (that reads as "rolling," which
        // is reserved for the roll-out later). Decelerates smoothly to a
        // stop, like a coin spun on a table losing momentum.
        const angle = SPIN_W0 * t - 0.5 * SPIN_K * t * t;
        const rad = (angle * Math.PI) / 180;
        const squash = Math.cos(rad);
        showFace(squash >= 0);
        coin.style.transform = `scaleX(${Math.abs(squash).toFixed(4)})`;

        // Net leftward drift, eased so it doesn't start/stop abruptly.
        const driftT = easeInOutCubic(Math.min(t / SPIN_DURATION, 1));
        const driftX = -DRIFT_DISTANCE * driftT;
        coinWrap.style.transform = `translate(${driftX.toFixed(2)}px, 0px)`;
      } else if (t <= T_PAUSE_END) {
        showFace(false);
        coin.style.transform = 'scaleX(1)';
        coinWrap.style.transform = `translate(${-DRIFT_DISTANCE}px, 0px)`;
        rimGlow.classList.add('coin-banner__rim-glow--visible');
      } else if (t <= T_JUMP_END) {
        const f = (t - T_PAUSE_END) / JUMP_DURATION;
        const hopY = -4 * JUMP_HEIGHT * f * (1 - f);
        coinWrap.style.transform = `translate(${-DRIFT_DISTANCE}px, ${hopY.toFixed(2)}px)`;
      } else if (t <= T_ROLL_END) {
        const localT = (t - T_JUMP_END) / ROLL_DURATION;
        const eased = easeInOutCubic(Math.min(localT, 1));
        const x = ROLL_DISTANCE * eased;
        const spin = ROLL_TURNS * 360 * eased;
        coinWrap.style.transform = `translate(${-DRIFT_DISTANCE}px, 0px)`;
        coin.style.transform = `translateX(${x.toFixed(2)}px) rotate(${spin.toFixed(2)}deg)`;

        // The ribbon is revealed in lockstep with how far the coin has
        // rolled -- it's quite literally pulled out from behind the coin.
        const hiddenWidth = Math.max(ROLL_DISTANCE - x, 0);
        ribbon.style.clipPath = `inset(0 ${hiddenWidth.toFixed(2)}px 0 0)`;
      } else {
        // Leave the inline transforms holding the exact resting values --
        // clearing them here, before React has actually committed the
        // `coin-banner--landed` class below, would snap the coin back to
        // its unstyled center position for a frame and then visibly ease
        // back out once the class (and its transition) finally lands. The
        // effect that clears them runs after that class is already in the
        // DOM, so the hand-off from inline style to CSS rule -- which holds
        // the same values -- is invisible.
        coinWrap.style.transform = `translate(${-DRIFT_DISTANCE}px, 0px)`;
        coin.style.transform = `translateX(${ROLL_DISTANCE}px) rotate(${(ROLL_TURNS * 360).toFixed(2)}deg)`;
        ribbon.style.clipPath = 'inset(0 0px 0 0)';
        rafId = null;
        setLanded(true);
        return;
      }

      rafId = requestAnimationFrame(frame);
    }

    function play() {
      if (rafId) cancelAnimationFrame(rafId);
      rimGlow.classList.remove('coin-banner__rim-glow--idle-pulse');
      layoutCenteredAssembly();
      showFace(false);
      coin.style.transform = 'scaleX(1)';
      coinWrap.style.transform = 'translate(0px, 0px)';
      // Fully hidden (not just the ROLL_DISTANCE-wide reveal section) so the
      // RIBBON_EXTRA_LEFT sliver -- which the roll-phase clip-path never
      // covers -- doesn't peek out during spin/pause/jump. It stays this way
      // until the roll phase's first frame below.
      ribbon.style.clipPath = 'inset(0 100% 0 0)';
      rimGlow.classList.remove('coin-banner__rim-glow--visible');
      startTime = null;
      rafId = requestAnimationFrame(frame);
    }

    if (started) {
      play();
      window.addEventListener('resize', layoutCenteredAssembly);
      return () => {
        window.removeEventListener('resize', layoutCenteredAssembly);
        if (rafId) cancelAnimationFrame(rafId);
      };
    }

    layoutIdle();
    rimGlow.classList.add('coin-banner__rim-glow--idle-pulse');
    rafId = requestAnimationFrame(idleFrame);
    window.addEventListener('resize', layoutIdle);
    const autoStartTimer = setTimeout(() => setStarted(true), AUTO_START_DELAY);
    return () => {
      window.removeEventListener('resize', layoutIdle);
      clearTimeout(autoStartTimer);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [active, started, sceneRef, setLanded, setStarted]);

  // Hands the coin's position off from the inline styles the reveal effect
  // was driving to the `coin-banner--landed` CSS rule holding the same
  // values -- deferred to its own effect so it runs after React has
  // actually committed that class, not before, which is what was causing
  // the coin to snap back to center for a frame right after landing.
  useEffect(() => {
    if (!landed) return;
    if (coinWrapRef.current) coinWrapRef.current.style.transform = '';
    if (coinRef.current) coinRef.current.style.transform = '';
  }, [landed]);

  function handleCoinClick() {
    if (!started) {
      setStarted(true);
      return;
    }
    if (minimized) {
      setMinimized(false);
      return;
    }
    // Reveal still mid-flight (started but not yet landed) -- ignore.
    // Falling through to the minimize below would apply the minimized
    // styling to a coin whose position is still being driven by the
    // reveal's own inline styles, not the CSS classes minimize expects.
    if (!landed) return;
    window.open(SPONSOR_URL, '_blank', 'noopener');
    setMinimized(true);
  }

  if (!active) return null;

  return (
    <div
      ref={sceneRef}
      className={`banner coin-banner ${minimized ? 'coin-banner--minimized' : landed ? 'coin-banner--landed' : ''}`.trim()}
      {...containerProps}
    >
      <div
        ref={tableShadowRef}
        className="coin-banner__table-shadow"
        aria-hidden="true"
      />

      <div
        ref={ribbonRef}
        className="coin-banner__ribbon"
        role="status"
        aria-live="polite"
      >
        <div className="coin-banner__ribbon-text">
          <span className="coin-banner__eyebrow">Proud Sponsor Of The</span>
          <a
            href={SPONSOR_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="coin-banner__title"
            tabIndex={expanded ? undefined : -1}
          >
            Charity of Champions
          </a>
          <span className="coin-banner__meta">Limited Edition &middot; 2026</span>
        </div>
      </div>

      <button
        type="button"
        ref={coinWrapRef}
        className="coin-banner__coin-wrap"
        onClick={handleCoinClick}
        tabIndex={minimized ? -1 : undefined}
        aria-label={
          started
            ? 'Visit the Charity of Champions website'
            : 'Reveal the Charity of Champions sponsorship'
        }
      >
        <div ref={coinRef} className="coin-banner__coin">
          <div
            ref={rimGlowRef}
            className="coin-banner__rim-glow"
            aria-hidden="true"
          />
          <div className="coin-banner__coin-face">
            {/* Both faces are always in the DOM, already decoded, and
                toggled via opacity -- see the component for why. */}
            <img
              ref={frontImgRef}
              src={FRONT_SRC}
              alt="Lux Financial limited edition commemorative coin"
              width={COIN_SIZE}
              height={COIN_SIZE}
              className="coin-banner__coin-img"
            />
            <img
              ref={backImgRef}
              src={BACK_SRC}
              alt=""
              aria-hidden="true"
              width={COIN_SIZE}
              height={COIN_SIZE}
              className="coin-banner__coin-img coin-banner__coin-img--back"
            />
          </div>
        </div>
      </button>

      <BannerPill
        visible={minimized}
        onClick={handleCoinClick}
        ariaLabel="View Charity of Champions sponsorship message"
        icon={
          <img
            src={FRONT_SRC}
            alt=""
            aria-hidden="true"
            width={24}
            height={24}
            className="banner-pill__icon"
          />
        }
        label="COC 2026"
      />
    </div>
  );
}
